import React, { useState, useCallback, useMemo } from "react";
import { Card, Form, Button, Table, Badge, ButtonGroup, Modal, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AiOutlineUpload,
  AiOutlineLink,
  AiOutlineUnorderedList,
  AiOutlineDelete,
  AiOutlineEdit,
} from "react-icons/ai";
import axios from "axios";
import WeekSelector from "../weekSelector";
import DriverSelector from "../../components/driverSelector";

const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const normalizeIdName = (name) => String(name || "").trim().toLowerCase();

const isSameIdentifierCard = (item, templateName, identifier) => {
  const card = String(item.cardNumber ?? item.card_number ?? "").trim();
  return (
    normalizeIdName(item.name) === normalizeIdName(templateName) &&
    card === String(identifier || "").trim()
  );
};

const mapIdentifiersPayload = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    name: String(item.name || "").trim(),
    cardNumber: String(item.cardNumber ?? item.card_number ?? "").trim(),
  }));

const skipReasonLabel = (reason, t) => {
  if (reason === "negative") return t("dataImportSkipNegative");
  if (reason === "filter") return t("dataImportSkipFilter");
  return t("dataImportSkipInvalid");
};

const formatCalcLabel = (row, t) => {
  if (row.status === "skipped") return skipReasonLabel(row.reason, t);
  if (row.calcType === "vat" && row.vatRate != null && row.gross != null) {
    return t("dataImportRowCalcVat", {
      gross: round2(row.gross).toFixed(2),
      rate: row.vatRate,
      result: round2(row.contribution).toFixed(2),
    });
  }
  if (row.gross != null) {
    return t("dataImportRowCalcFull", { gross: round2(row.gross).toFixed(2) });
  }
  return "—";
};

const collectPreviewColumns = (rows) => {
  const cols = [];
  const seen = new Set();
  for (const row of rows) {
    if (!row?.preview) continue;
    for (const key of Object.keys(row.preview)) {
      if (!seen.has(key)) {
        seen.add(key);
        cols.push(key);
      }
    }
  }
  return cols;
};

const rowResultAmount = (row) => {
  if (row.status === "included" && row.contribution != null) return round2(row.contribution).toFixed(2);
  return "—";
};

const IdentifierDetailsModal = ({ show, group, onHide, t, percentValid, pctNum }) => {
  const detailRows = useMemo(
    () => (Array.isArray(group?.rows) ? [...group.rows].sort((a, b) => a.line - b.line) : []),
    [group]
  );
  const previewColumns = useMemo(() => collectPreviewColumns(detailRows), [detailRows]);
  const includedCount = detailRows.filter((r) => r.status === "included").length;
  const skippedCount = detailRows.filter((r) => r.status === "skipped").length;
  const appliedTotal =
    group && percentValid ? round2((group.sumRaw * pctNum) / 100).toFixed(2) : null;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable dialogClassName="import-row-details-modal">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          {t("dataImportRowDetailsModalTitle")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="import-row-details-modal__body">
        {group && (
          <div className="d-flex flex-wrap gap-3 mb-3 pb-3 border-bottom small">
            <div>
              <span className="text-muted">{t("dataImportIdentifier")}: </span>
              <code>{group.identifier}</code>
            </div>
            <div>
              <span className="text-muted">{t("dataImportRowDetailsTotal")}: </span>
              <strong>{round2(group.sumRaw).toFixed(2)}</strong>
            </div>
            {appliedTotal != null && (
              <div>
                <span className="text-muted">{t("dataImportSumAfterPercent")}: </span>
                <strong>{appliedTotal}</strong>
              </div>
            )}
            <div>
              <Badge bg="success" className="me-1">{includedCount}</Badge>
              <span className="text-muted">{t("dataImportRowIncluded")}</span>
              {skippedCount > 0 && (
                <>
                  <Badge bg="secondary" className="ms-2 me-1">{skippedCount}</Badge>
                  <span className="text-muted">{t("dataImportRowSkipped")}</span>
                </>
              )}
            </div>
          </div>
        )}

        {detailRows.length === 0 ? (
          <div className="text-muted">{t("dataImportNoRowDetails")}</div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="mb-0 import-row-details-table">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 56 }}>#</th>
                  {previewColumns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                  <th className="text-end">{t("dataImportRowDetailsGross")}</th>
                  <th>{t("dataImportRowDetailsCalc")}</th>
                  <th className="text-end">{t("dataImportRowDetailsResult")}</th>
                  <th style={{ width: 110 }}>{t("dataImportStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((row, idx) => (
                  <tr
                    key={`${row.line}-${idx}`}
                    className={row.status === "skipped" ? "table-secondary text-muted" : ""}
                  >
                    <td>{row.line}</td>
                    {previewColumns.map((col) => (
                      <td key={col} className="small">
                        {row.preview?.[col] || "—"}
                      </td>
                    ))}
                    <td className="text-end text-nowrap">
                      {row.gross != null ? round2(row.gross).toFixed(2) : "—"}
                    </td>
                    <td className="small">{formatCalcLabel(row, t)}</td>
                    <td className="text-end text-nowrap fw-medium">
                      {rowResultAmount(row)}
                    </td>
                    <td>
                      {row.status === "included" ? (
                        <Badge bg="success">{t("dataImportRowIncluded")}</Badge>
                      ) : (
                        <Badge bg="secondary">{t("dataImportRowSkipped")}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan={previewColumns.length + 3} className="text-end fw-semibold">
                    {t("dataImportRowDetailsTotal")}
                  </td>
                  <td className="text-end fw-semibold text-nowrap">
                    {group ? round2(group.sumRaw).toFixed(2) : "—"}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const DataImport = () => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState("");
  const [template, setTemplate] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [percent, setPercent] = useState("100");
  const [actionType, setActionType] = useState("");
  const [description, setDescription] = useState("");
  const [applying, setApplying] = useState(false);

  const [bindModal, setBindModal] = useState({
    show: false,
    identifier: "",
    drivers: [],
    driverOption: null,
    busy: false,
  });
  const [detailsModal, setDetailsModal] = useState({ show: false, group: null });

  const pctNum = parseFloat(String(percent).replace(",", "."));
  const percentValid = Number.isFinite(pctNum) && pctNum > 0;

  const closeBindModal = () => {
    setBindModal({
      show: false,
      identifier: "",
      drivers: [],
      driverOption: null,
      busy: false,
    });
  };

  const runParse = useCallback(async (f, options = {}) => {
    if (!f) return null;
    const { keepSelection = false, refreshBindIdentifier = null } = options;
    setParsing(true);
    setParseError("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post("/api/import-data/parse", fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setTemplate(data.template);
      const gr = data.groups || [];
      setGroups(gr);
      if (!keepSelection) {
        setSelectedIds(new Set(gr.filter((g) => g.status === "matched").map((g) => g.identifier)));
      } else {
        setSelectedIds((prev) => {
          const matched = new Set(gr.filter((g) => g.status === "matched").map((g) => g.identifier));
          const next = new Set([...prev].filter((id) => matched.has(id)));
          // Після bind/rebind ново-matched картку одразу додаємо до вибору
          if (refreshBindIdentifier && matched.has(refreshBindIdentifier)) {
            next.add(refreshBindIdentifier);
          }
          return next;
        });
      }
      if (refreshBindIdentifier) {
        const updated = gr.find((g) => g.identifier === refreshBindIdentifier);
        setBindModal((m) =>
          m.show && m.identifier === refreshBindIdentifier
            ? { ...m, drivers: updated?.drivers || [], busy: false }
            : m
        );
      }
      return data;
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setParseError(msg);
      setTemplate(null);
      setGroups([]);
      setSelectedIds(new Set());
      return null;
    } finally {
      setParsing(false);
    }
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setParseError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    await runParse(file);
  };

  const toggleRow = (identifier) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(identifier)) next.delete(identifier);
      else next.add(identifier);
      return next;
    });
  };

  const toggleAllMatched = () => {
    const matched = groups.filter((g) => g.status === "matched").map((g) => g.identifier);
    const allSelected = matched.length > 0 && matched.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(matched));
    }
  };

  const openBind = (group) => {
    setBindModal({
      show: true,
      identifier: group.identifier,
      drivers: Array.isArray(group.drivers) ? group.drivers : [],
      driverOption: null,
      busy: false,
    });
  };

  const openDetails = (group) => {
    setDetailsModal({ show: true, group });
  };

  const closeDetails = () => {
    setDetailsModal({ show: false, group: null });
  };

  const removeCardFromDriver = async (driverId, identifier, templateName) => {
    const { data: existing } = await axios.get(`/api/users/${driverId}/identifiers`, {
      headers: authHeaders(),
    });
    const next = mapIdentifiersPayload(existing).filter(
      (item) => !isSameIdentifierCard(item, templateName, identifier)
    );
    await axios.put(
      `/api/users/${driverId}/identifiers`,
      { identifiers: next },
      { headers: authHeaders() }
    );
  };

  const addCardToDriver = async (driverId, identifier, templateName) => {
    const { data: existing } = await axios.get(`/api/users/${driverId}/identifiers`, {
      headers: authHeaders(),
    });
    const list = mapIdentifiersPayload(existing);
    const already = list.some((item) => isSameIdentifierCard(item, templateName, identifier));
    if (already) return;
    // Зберігаємо name так само, як парсер звіряє: LOWER(TRIM(name)) = name_normalized
    const cardName = String(templateName || "").trim();
    const payload = [
      ...list,
      {
        name: cardName,
        cardNumber: String(identifier || "").trim(),
      },
    ];
    await axios.put(
      `/api/users/${driverId}/identifiers`,
      { identifiers: payload },
      { headers: authHeaders() }
    );
  };

  const cardTemplateName = () =>
    template?.name_normalized || normalizeIdName(template?.name) || String(template?.name || "").trim();

  const handleUnbindDriver = async (driverId) => {
    if (!template || !bindModal.identifier || bindModal.busy) return;
    setBindModal((m) => ({ ...m, busy: true }));
    try {
      await removeCardFromDriver(driverId, bindModal.identifier, cardTemplateName());
      if (file) {
        await runParse(file, {
          keepSelection: true,
          refreshBindIdentifier: bindModal.identifier,
        });
      }
    } catch (err) {
      alert(err.response?.data?.error || err.message || t("dataImportUnbindError"));
      setBindModal((m) => ({ ...m, busy: false }));
    }
  };

  const saveBind = async () => {
    if (!template || !bindModal.identifier || !bindModal.driverOption?.value || bindModal.busy) {
      return;
    }
    const driverId = bindModal.driverOption.value;
    setBindModal((m) => ({ ...m, busy: true }));
    try {
      await addCardToDriver(driverId, bindModal.identifier, cardTemplateName());
      setBindModal((m) => ({ ...m, driverOption: null }));
      if (file) {
        await runParse(file, {
          keepSelection: true,
          refreshBindIdentifier: bindModal.identifier,
        });
      }
    } catch (err) {
      alert(err.response?.data?.error || err.message || t("dataImportBindError"));
      setBindModal((m) => ({ ...m, busy: false }));
    }
  };

  const saveRebind = async () => {
    if (!template || !bindModal.identifier || !bindModal.driverOption?.value || bindModal.busy) {
      return;
    }
    const newDriverId = bindModal.driverOption.value;
    const currentDrivers = bindModal.drivers || [];
    setBindModal((m) => ({ ...m, busy: true }));
    try {
      for (const d of currentDrivers) {
        if (Number(d.driver_id) === Number(newDriverId)) continue;
        await removeCardFromDriver(d.driver_id, bindModal.identifier, cardTemplateName());
      }
      await addCardToDriver(newDriverId, bindModal.identifier, cardTemplateName());
      setBindModal((m) => ({ ...m, driverOption: null }));
      if (file) {
        await runParse(file, {
          keepSelection: true,
          refreshBindIdentifier: bindModal.identifier,
        });
      }
    } catch (err) {
      alert(err.response?.data?.error || err.message || t("dataImportBindError"));
      setBindModal((m) => ({ ...m, busy: false }));
    }
  };

  const handleApply = async () => {
    if (!template || !selectedWeek || !actionType || !description.trim() || !percentValid) {
      alert(t("dataImportApplyValidation"));
      return;
    }
    const entries = groups
      .filter((g) => g.status === "matched" && selectedIds.has(g.identifier))
      .map((g) => ({
        driver_id: g.driver_id,
        amountBase: g.sumRaw,
        identifier: g.identifier,
      }));
    if (entries.length === 0) {
      alert(t("dataImportApplyNoRows"));
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(
        "/api/import-data/apply",
        {
          week_number: selectedWeek,
          percent: pctNum,
          actionType,
          description: description.trim(),
          templateId: template.id,
          entries,
        },
        { headers: authHeaders() }
      );
      const bad = (data.results || []).filter((r) => !r.ok);
      if (bad.length === 0) {
        alert(t("dataImportApplySuccess"));
      } else {
        alert(
          t("dataImportApplyPartial") +
            "\n" +
            bad.map((b) => `${b.driver_id}: ${b.error || "?"}`).join("\n")
        );
      }
      if (file) await runParse(file);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setApplying(false);
    }
  };

  const statusBadge = (status) => {
    if (status === "matched") return <Badge bg="success">{t("dataImportStatusMatched")}</Badge>;
    if (status === "ambiguous") return <Badge bg="warning" text="dark">{t("dataImportStatusAmbiguous")}</Badge>;
    return <Badge bg="secondary">{t("dataImportStatusUnmatched")}</Badge>;
  };

  const matchedCount = groups.filter((g) => g.status === "matched").length;

  return (
    <div className="mb-4">
      <h2 className="mb-2">{t("dataImport")}</h2>
      <p className="text-muted small mb-3">
        {t("dataImportUploadHint")}{" "}
        <Link to="/admin/import-templates">{t("dataImportLinkToTemplates")}</Link>
      </p>

      <Card className="mb-4">
        <Card.Header>{t("dataImportUpload")}</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t("dataImportFile")}</Form.Label>
              <Form.Control
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileChange}
              />
            </Form.Group>
            <Button type="submit" variant="primary" disabled={!file || parsing}>
              {parsing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("dataImportParsing")}
                </>
              ) : (
                <>
                  <AiOutlineUpload className="me-2" />
                  {t("dataImportUploadButton")}
                </>
              )}
            </Button>
          </Form>
          {parseError && (
            <div className="text-danger small mt-3" role="alert">
              {parseError}
            </div>
          )}
        </Card.Body>
      </Card>

      {template && (
        <>
          {groups.length === 0 && (
            <Card className="mb-4">
              <Card.Body className="text-muted">{t("dataImportNoRows")}</Card.Body>
            </Card>
          )}
          {groups.length > 0 && (
          <Card className="mb-4">
            <Card.Header>{t("dataImportActions")}</Card.Header>
            <Card.Body>
              <Form>
                <div className="row g-3">
                  <div className="col-md-4">
                    <Form.Label className="small text-muted">{t("selectWeek")}</Form.Label>
                    <WeekSelector
                      selectedWeek={selectedWeek}
                      setSelectedWeek={setSelectedWeek}
                      className="mb-0"
                    />
                  </div>
                  <div className="col-md-4">
                    <Form.Label className="d-block small text-muted">{t("dataImportActionType")}</Form.Label>
                    <ButtonGroup>
                      <Button
                        variant={actionType === "other" ? "primary" : "outline-primary"}
                        size="sm"
                        type="button"
                        onClick={() => setActionType((a) => (a === "other" ? "" : "other"))}
                      >
                        {t("dataImportActionExtraExpenses")}
                      </Button>
                      <Button
                        variant={actionType === "refunds" ? "primary" : "outline-primary"}
                        size="sm"
                        type="button"
                        onClick={() => setActionType((a) => (a === "refunds" ? "" : "refunds"))}
                      >
                        {t("dataImportActionReimbursement")}
                      </Button>
                    </ButtonGroup>
                  </div>
                  <div className="col-md-4">
                    <Form.Label className="small text-muted">{t("dataImportPercentReceived")}</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="decimal"
                      size="sm"
                      value={percent}
                      onChange={(e) => setPercent(e.target.value)}
                      placeholder="100"
                    />
                  </div>
                  <div className="col-12">
                    <Form.Label className="small text-muted">{t("dataImportDescription")}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("dataImportDescriptionPlaceholder")}
                    />
                  </div>
                  <div className="col-12">
                    <Button
                      variant="success"
                      type="button"
                      disabled={
                        applying ||
                        !selectedWeek ||
                        !actionType ||
                        !description.trim() ||
                        !percentValid ||
                        !template
                      }
                      onClick={handleApply}
                    >
                      {applying ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {t("dataImportApplying")}
                        </>
                      ) : (
                        t("dataImportApply")
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            </Card.Body>
          </Card>
          )}

          {groups.length > 0 && (
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span>{t("dataImportParsedTable")}</span>
                <Badge bg="info">{template.name}</Badge>
              </div>
              <Badge bg="secondary">
                {selectedIds.size} / {groups.length} · {t("dataImportMatchedHint")}: {matchedCount}
              </Badge>
            </Card.Header>
            <Card.Body className="p-0 overflow-auto">
              <Table responsive striped bordered hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 44 }}>
                      <Form.Check
                        type="checkbox"
                        checked={
                          matchedCount > 0 &&
                          groups.filter((g) => g.status === "matched").every((g) => selectedIds.has(g.identifier))
                        }
                        onChange={toggleAllMatched}
                        aria-label={t("dataImportSelectAll")}
                      />
                    </th>
                    <th>{t("dataImportIdentifier")}</th>
                    <th>{t("dataImportSumFile")}</th>
                    <th>{t("dataImportSumAfterPercent")}</th>
                    <th>{t("dataImportDriverLabel")}</th>
                    <th>{t("dataImportStatus")}</th>
                    <th style={{ width: 200 }}>{t("dataImportBind")}</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((row) => {
                    const applied = percentValid ? round2((row.sumRaw * pctNum) / 100) : null;
                    const canSelect = row.status === "matched";
                    return (
                      <tr
                        key={row.identifier}
                        className={!canSelect || !selectedIds.has(row.identifier) ? "table-secondary opacity-75" : ""}
                      >
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.has(row.identifier)}
                            onChange={() => toggleRow(row.identifier)}
                            disabled={!canSelect}
                            aria-label={t("dataImportSelectRow")}
                          />
                        </td>
                        <td>
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none d-inline-flex align-items-center gap-1"
                            onClick={() => openDetails(row)}
                            title={t("dataImportRowDetailsShow")}
                          >
                            <code>{row.identifier}</code>
                            <AiOutlineUnorderedList className="text-muted flex-shrink-0" size={14} />
                          </Button>
                        </td>
                        <td>{round2(row.sumRaw).toFixed(2)}</td>
                        <td>{applied != null ? applied.toFixed(2) : "—"}</td>
                        <td>
                          {Array.isArray(row.drivers) && row.drivers.length > 1 ? (
                            <ul className="mb-0 ps-3 small">
                              {row.drivers.map((d) => (
                                <li key={d.driver_id}>{d.label}</li>
                              ))}
                            </ul>
                          ) : (
                            row.driverLabel
                          )}
                        </td>
                        <td>{statusBadge(row.status)}</td>
                        <td>
                          {row.status === "unmatched" ? (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => openBind(row)}
                            >
                              <AiOutlineLink className="me-1" />
                              {t("dataImportBindButton")}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant={row.status === "ambiguous" ? "outline-warning" : "outline-secondary"}
                              onClick={() => openBind(row)}
                            >
                              <AiOutlineEdit className="me-1" />
                              {row.status === "ambiguous"
                                ? t("dataImportResolveBind")
                                : t("dataImportManageBind")}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
          )}
        </>
      )}

      <IdentifierDetailsModal
        show={detailsModal.show}
        group={detailsModal.group}
        onHide={closeDetails}
        t={t}
        percentValid={percentValid}
        pctNum={pctNum}
      />

      <Modal show={bindModal.show} onHide={closeBindModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {bindModal.drivers.length > 1
              ? t("dataImportResolveBind")
              : bindModal.drivers.length === 1
                ? t("dataImportManageBind")
                : t("dataImportBind")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3">
            {t("dataImportBindHint")}: <code>{bindModal.identifier}</code>
          </p>

          {bindModal.drivers.length > 0 && (
            <div className="mb-3">
              <div className="fw-semibold small mb-2">{t("dataImportCurrentBindings")}</div>
              {bindModal.drivers.length > 1 && (
                <p className="small text-warning mb-2">{t("dataImportAmbiguousHint")}</p>
              )}
              <ul className="list-group list-group-flush border rounded">
                {bindModal.drivers.map((d) => (
                  <li
                    key={d.driver_id}
                    className="list-group-item d-flex justify-content-between align-items-center gap-2 py-2"
                  >
                    <span>{d.label || `${d.firstName} ${d.lastName}`}</span>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={bindModal.busy}
                      onClick={() => handleUnbindDriver(d.driver_id)}
                      title={t("dataImportUnbindButton")}
                    >
                      <AiOutlineDelete className="me-1" />
                      {t("dataImportUnbindButton")}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mb-1 fw-semibold small">{t("dataImportBindToDriver")}</div>
          <p className="small text-muted mb-2">
            {bindModal.drivers.length > 0
              ? t("dataImportRebindHint")
              : t("dataImportBindNewHint")}
          </p>
          <DriverSelector
            activeOnly
            selectedDriver={bindModal.driverOption}
            setSelectedDriver={(opt) => setBindModal((m) => ({ ...m, driverOption: opt }))}
          />
        </Modal.Body>
        <Modal.Footer className="flex-wrap gap-2">
          <Button variant="secondary" onClick={closeBindModal} disabled={bindModal.busy}>
            {t("cancel")}
          </Button>
          {bindModal.drivers.length > 0 ? (
            <Button
              variant="primary"
              onClick={saveRebind}
              disabled={!bindModal.driverOption || bindModal.busy}
            >
              {bindModal.busy ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <AiOutlineLink className="me-1" />
              )}
              {t("dataImportRebindButton")}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={saveBind}
              disabled={!bindModal.driverOption || bindModal.busy}
            >
              {bindModal.busy ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <AiOutlineLink className="me-1" />
              )}
              {t("save")}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DataImport;
